import { Button, Heading, IconButton, Input, Separator, Text, toast } from "@overmap-ai/blocks"
import { generateAbbreviation } from "@overmap-ai/common"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { type FormEventHandler, memo, useCallback, useState } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { LuDynamicIcon } from "../../components/Icons/LuDynamicIcon"
import type { IconType } from "../../components/Icons/typings"
import { IconColorPickerPopover } from "../../components/Popovers/IconColorPickerPopover"
import { DEFAULT_COLOR } from "../../constants"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconNameMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/teams/create")

export const CreateTeamView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const navigate = useNavigate()
	const [icon, setIcon] = useState<IconType>(modelIconNameMapping.team)
	const [color, setColor] = useState<string>(DEFAULT_COLOR)
	const [name, setName] = useState<string>("")

	const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
		(e) => {
			e.preventDefault()
			e.stopPropagation()

			if (!name) {
				toast.error({
					title: "Team name is required",
					description: "Please enter a name for the team",
				})
				return
			}

			const [offlineTeam, _promise] = sdk.teams.add({
				name: name,
				abbr: generateAbbreviation(name),
				icon: icon,
				color: color,
				parent: null,
				organization: organizationId,
			})

			setName("")
			setIcon(modelIconNameMapping.team)
			setColor(DEFAULT_COLOR)

			void navigate({
				from: "/$organizationId/settings/teams/create",
				to: "/$organizationId/settings/teams/$teamId",
				params: (prev) => ({
					...prev,
					teamId: offlineTeam.uuid,
				}),
			})
		},
		[organizationId, navigate, icon, color, name],
	)

	const handleNameChange = useChangeEventHandler(setName)

	const handleColorChange = useCallback((color: string | null) => {
		if (!color) return
		setColor(color)
	}, [])

	const handleIconChange = useCallback((icon: IconType | null) => {
		if (!icon) return
		setIcon(icon)
	}, [])

	return (
		<ViewContainer maxWidth="2xl">
			<section className="flex h-max w-full flex-col gap-4">
				<div className="flex h-max w-full flex-col gap-1">
					<Heading size="xl">Create team</Heading>

					<Text size="sm" accentColor="base">
						Create a new team to manage object visibility for groups of users within the organization
					</Text>
				</div>

				<Separator size="full" />

				<form className="flex w-full flex-col gap-4" onSubmit={handleSubmit}>
					<div className="flex w-full gap-2">
						<IconColorPickerPopover
							className="w-[340px]"
							color={color}
							selectedIcon={icon}
							onColorChange={handleColorChange}
							onIconChange={handleIconChange}
							size="sm"
						>
							<IconButton aria-label="icon" accentColor="base" variant="soft">
								<LuDynamicIcon icon={icon} color={color} />
							</IconButton>
						</IconColorPickerPopover>

						<Input.Root variant="outline" className="w-full max-w-[300px]">
							<Input.Field placeholder="Enter a name" value={name} onChange={handleNameChange} />
						</Input.Root>
					</div>

					<Button className="w-max" type="submit" variant="surface">
						Create
					</Button>
				</form>
			</section>
		</ViewContainer>
	)
})
CreateTeamView.displayName = "CreateTeamView"
